/**
 * Client HTTP da agenda da Central de Amparo.
 * Usa a mesma base da IA-API (`iaApiBase`) + path `/centralamparo`
 * (agendamentos, disponibilidade, profissionais, elegibilidade).
 * Auth: Bearer JWT do usuário Supabase (rotas `/centralamparo`).
 * Pessoa por CPF do paciente (sem PASI): ver `ferramentasApi.getPessoaCpf`.
 */
import { IA_API_CENTRAL_AMPARO_BASE } from './iaApiBase'
import { iaApiFetch, unwrapIaApiData } from './iaApiClient'
import type {
  SupportCenterAgendaCancelInput,
  SupportCenterAgendaCreateApiBody,
  SupportCenterAgendaDisponibilidadeProfissional,
  SupportCenterAgendaProfessional,
  SupportCenterAgendaRescheduleApiBody,
  SupportCenterAgendaSlot,
  SupportCenterElegibilidadeCobertura,
  SupportCenterElegibilidadeEspecialidade,
  SupportCenterElegibilidadeProfissional,
  SupportCenterElegibilidadePessoa,
  SupportCenterElegibilidadeResult,
} from '../types/supportCenterAgenda'

export type {
  SupportCenterAgendaCancelInput,
  SupportCenterAgendaCreateApiBody,
  SupportCenterAgendaDisponibilidadeProfissional,
  SupportCenterAgendaProfessional,
  SupportCenterAgendaRescheduleApiBody,
  SupportCenterAgendaSlot,
  SupportCenterElegibilidadeCobertura,
  SupportCenterElegibilidadeEspecialidade,
  SupportCenterElegibilidadeProfissional,
  SupportCenterElegibilidadePessoa,
  SupportCenterElegibilidadeResult,
} from '../types/supportCenterAgenda'

async function agendaFetch<T>(
  path: string,
  init: RequestInit & { method?: string } = {}
): Promise<T> {
  return iaApiFetch<T>(IA_API_CENTRAL_AMPARO_BASE, path, init, {
    apiLabel: 'agenda',
    sessionExpiredMessage: 'Sessão expirada. Faça login novamente para usar a agenda.',
  })
}

function asRecordArray(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) {
    return payload.filter((item) => item && typeof item === 'object') as Record<string, unknown>[]
  }
  if (payload && typeof payload !== 'object') return []
  const data = (payload as Record<string, unknown>).data
  if (Array.isArray(data)) {
    return data.filter((item) => item && typeof item === 'object') as Record<string, unknown>[]
  }
  return []
}

function toBrDate(isoOrLocal: string): string {
  const raw = isoOrLocal.includes('T') ? isoOrLocal.slice(0, 10) : isoOrLocal.slice(0, 10)
  const [y, m, d] = raw.split('-')
  if (y && m && d) return `${d}/${m}/${y}`
  return raw
}

/** Converte dd/mm/aaaa → yyyy-mm-dd; retorna null se inválido. */
function brDateToYmd(brDate: string): string | null {
  const match = brDate.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return null
  const [, d, m, y] = match
  return `${y}-${m}-${d}`
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** POST /agendamentos e /reagendar: email_paciente é uma string, nunca lista. */
function withSinglePatientEmail<T extends { email_paciente: string }>(body: T): T {
  const raw: unknown = body.email_paciente
  if (typeof raw !== 'string') {
    throw new Error('Informe apenas um e-mail do paciente.')
  }
  const parts = raw
    .split(/[,;\s]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
  if (parts.length !== 1) {
    throw new Error(
      parts.length === 0
        ? 'Informe o e-mail do paciente.'
        : 'Informe apenas um e-mail do paciente.'
    )
  }
  return { ...body, email_paciente: parts[0] }
}

/**
 * Achata `slots[] → datas[] → horas[]` em slots internos { data, horario, scheduled_at }.
 * Filtra pelo intervalo yyyy-mm-dd (inclusive).
 */
function flattenDisponibilidadeSlots(
  semanas: unknown,
  rangeStartYmd: string,
  rangeEndYmd: string
): SupportCenterAgendaSlot[] {
  if (!Array.isArray(semanas)) return []

  const start = rangeStartYmd.slice(0, 10)
  const end = rangeEndYmd.slice(0, 10)
  const out: SupportCenterAgendaSlot[] = []

  for (const semanaItem of semanas) {
    if (!semanaItem || typeof semanaItem !== 'object') continue
    const datasRaw = (semanaItem as Record<string, unknown>).datas
    if (!Array.isArray(datasRaw)) continue

    for (const dataItem of datasRaw) {
      if (!dataItem || typeof dataItem !== 'object') continue
      const row = dataItem as Record<string, unknown>
      const brData = String(row.data ?? '').trim()
      const ymd = brDateToYmd(brData)
      if (!ymd || ymd < start || ymd > end) continue

      const horasRaw = row.horas
      if (!Array.isArray(horasRaw)) continue

      for (const horaRaw of horasRaw) {
        const hour = typeof horaRaw === 'number' ? horaRaw : Number(horaRaw)
        if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue
        const horario = `${pad2(hour)}:00`
        out.push({
          data: brData,
          horario,
          scheduled_at: `${ymd}T${horario}:00`,
        })
      }
    }
  }

  return out.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))
}

function mapElegibilidadePessoa(raw: unknown): SupportCenterElegibilidadePessoa {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { name: null, tax_id_number: null, captured_birth_date: null }
  }
  const row = raw as Record<string, unknown>
  const birth =
    row.captured_birth_date ?? row.birth_date ?? row.data_nascimento ?? row.nascimento ?? null
  return {
    name: typeof row.name === 'string' ? row.name.trim() || null : null,
    tax_id_number:
      typeof row.tax_id_number === 'string'
        ? row.tax_id_number
        : typeof row.cpf === 'string'
          ? row.cpf
          : null,
    captured_birth_date: typeof birth === 'string' ? birth.slice(0, 10) || null : null,
  }
}

function mapProfissionais(raw: unknown): SupportCenterAgendaProfessional[] {
  if (!Array.isArray(raw)) return []
  const rows: SupportCenterAgendaProfessional[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const id = String(row.id ?? '').trim()
    const email = String(row.email ?? '').trim()
    if (!id || !email) continue
    rows.push({
      id,
      name: String(row.name ?? '').trim() || email,
      email,
    })
  }
  return rows
}

function parseOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(String(value).trim())
  return Number.isFinite(n) ? n : null
}

function mapCoberturas(raw: unknown): SupportCenterElegibilidadeCobertura[] {
  if (!Array.isArray(raw)) return []
  const rows: SupportCenterElegibilidadeCobertura[] = []
  const seen = new Set<string>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const codeRaw = row.CodCobertura ?? row.codCobertura ?? row.code ?? row.codigo
    if (codeRaw === null || codeRaw === undefined || codeRaw === '') continue
    const code = String(codeRaw).trim()
    if (!code || seen.has(code)) continue
    seen.add(code)
    const nameRaw = row.DscCobertura ?? row.dscCobertura ?? row.name ?? row.descricao ?? ''
    const name = typeof nameRaw === 'string' ? nameRaw.trim() : String(nameRaw).trim()
    rows.push({
      code,
      name: name || code,
      codApolice: parseOptionalNumber(row.CodApolice ?? row.codApolice ?? row.cod_apolice),
      codConvenio: parseOptionalNumber(row.CodConvenio ?? row.codConvenio ?? row.cod_convenio),
      codFornecedorAssistencia: parseOptionalNumber(
        row.CodFornecedorAssistencia ??
          row.codFornecedorAssistencia ??
          row.cod_fornecedor_assistencia
      ),
      codCorretor: parseOptionalNumber(row.CodCorretor ?? row.codCorretor ?? row.cod_corretor),
      codCliente: parseOptionalNumber(row.CodCliente ?? row.codCliente ?? row.cod_cliente),
    })
  }
  return rows
}

function mapEspecialidadesElegiveis(raw: unknown): SupportCenterElegibilidadeEspecialidade[] {
  if (!Array.isArray(raw)) return []
  const out: SupportCenterElegibilidadeEspecialidade[] = []
  for (const item of raw) {
    if (typeof item === 'string' || typeof item === 'number') {
      const id = String(item).trim()
      if (!id) continue
      out.push({ id, name: id, form_kind: null, coberturas: [], profissionais: [] })
      continue
    }
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const id = String(
      row.id ?? row.especialidade_id ?? row.specialty_id ?? ''
    ).trim()
    if (!id) continue
    out.push({
      id,
      name: String(row.name ?? '').trim() || id,
      form_kind: typeof row.form_kind === 'string' ? row.form_kind : null,
      coberturas: mapCoberturas(row.coberturas ?? row.coverages),
      profissionais: mapProfissionais(row.profissionais ?? row.professionals),
    })
  }
  return out
}

/**
 * Escolhe a cobertura da especialidade selecionada para o POST de agendamento.
 * Preferência: código igual a `codigoAssistencia`; senão a primeira da especialidade.
 */
export function pickCoverageForAppointment(
  especialidades: SupportCenterElegibilidadeEspecialidade[] | null | undefined,
  specializationId: string,
  codigoAssistencia?: string | null
): SupportCenterElegibilidadeCobertura | null {
  if (!especialidades?.length || !specializationId) return null
  const spec = especialidades.find((item) => item.id === specializationId)
  const coberturas = spec?.coberturas ?? []
  if (coberturas.length === 0) return null
  const code = (codigoAssistencia ?? '').trim()
  if (code) {
    const matched = coberturas.find((c) => c.code.trim() === code)
    if (matched) return matched
  }
  return coberturas[0] ?? null
}

export const supportCenterAgendaApi = {
  /**
   * GET /centralamparo/elegibilidade?cpf= — PASI + coberturas × especialidades
   * (cada uma com profissionais/pessoas) + pessoa. Sem match de cobertura → HTTP 422.
   */
  async getElegibilidade(cpf: string): Promise<SupportCenterElegibilidadeResult> {
    const digits = cpf.replace(/\D/g, '').slice(0, 11)
    if (digits.length !== 11) {
      throw new Error('Informe um CPF com 11 dígitos.')
    }
    const query = new URLSearchParams({ cpf: digits })
    const payload = await agendaFetch<unknown>(`/elegibilidade?${query}`, { method: 'GET' })
    const data = unwrapIaApiData(payload)
    if (!data || typeof data !== 'object') {
      throw new Error('Não foi possível consultar a elegibilidade. Tente novamente.')
    }
    const root = data as Record<string, unknown>
    const pessoaRaw = root.pessoa ?? root.person ?? root
    const specsRaw =
      root.especialidades_elegiveis ??
      root.especialidades ??
      []
    const especialidades = mapEspecialidadesElegiveis(specsRaw)
    const especialidadeIds = especialidades.map((spec) => spec.id)
    return {
      pessoa: mapElegibilidadePessoa(pessoaRaw),
      especialidadeIds,
      especialidades,
    }
  },

  async listProfissionais(specialtyId: string): Promise<SupportCenterAgendaProfessional[]> {
    const query = new URLSearchParams({
      especialidade_id: specialtyId,
    })
    const payload = await agendaFetch<unknown>(`/profissionais?${query}`, { method: 'GET' })
    const data = unwrapIaApiData(payload)
    let rows: Record<string, unknown>[] = []
    if (Array.isArray(data)) {
      rows = asRecordArray(data)
    } else if (data && typeof data === 'object') {
      const nested = (data as Record<string, unknown>).profissionais
      rows = Array.isArray(nested) ? asRecordArray(nested) : []
    }
    return rows
      .map((row) => ({
        id: String(row.id ?? ''),
        name: String(row.name ?? '').trim() || String(row.email ?? ''),
        email: String(row.email ?? '').trim(),
      }))
      .filter((row) => row.id && row.email)
  },

  async listDisponibilidade(input: {
    /** UUID do profissional — obrigatório para slots de agendamento. */
    professionalId: string
    dataInicio: string
    dataFim: string
  }): Promise<SupportCenterAgendaSlot[]> {
    const professionalId = input.professionalId.trim()
    if (!professionalId) {
      throw new Error('Informe o profissional (id_profissional) para consultar disponibilidade.')
    }

    const query = new URLSearchParams({
      id_profissional: professionalId,
      data_inicio: toBrDate(input.dataInicio),
      data_fim: toBrDate(input.dataFim),
    })
    const payload = await agendaFetch<unknown>(`/disponibilidade?${query}`, { method: 'GET' })
    const data = unwrapIaApiData(payload)
    if (!data || typeof data !== 'object') return []

    const root = data as Record<string, unknown>
    const profissionaisRaw = root.profissionais
    if (!Array.isArray(profissionaisRaw)) return []

    const profissional = profissionaisRaw.find((item) => {
      if (!item || typeof item !== 'object') return false
      return String((item as Record<string, unknown>).id ?? '') === professionalId
    }) as SupportCenterAgendaDisponibilidadeProfissional | undefined

    if (!profissional) return []

    return flattenDisponibilidadeSlots(
      profissional.slots,
      input.dataInicio,
      input.dataFim
    )
  },

  async create(body: SupportCenterAgendaCreateApiBody): Promise<Record<string, unknown>> {
    const payload = await agendaFetch<unknown>('/agendamentos', {
      method: 'POST',
      body: JSON.stringify(withSinglePatientEmail(body)),
    })
    const data = unwrapIaApiData(payload)
    if (!data || typeof data !== 'object') {
      throw new Error('Não foi possível confirmar o agendamento. Tente novamente.')
    }
    const row = data as Record<string, unknown>
    if (row.appointment && typeof row.appointment === 'object' && !Array.isArray(row.appointment)) {
      return row.appointment as Record<string, unknown>
    }
    return row
  },

  /**
   * POST /agendamentos/{id}/reagendar — cancela o antigo e cria o novo (Outlook + banco).
   * Resposta: { appointment, cancelled_appointment, triage }.
   * data.triage é somente leitura; o reagendamento não grava status na triagem.
   */
  async reschedule(
    id: string,
    body: SupportCenterAgendaRescheduleApiBody
  ): Promise<{
    appointment: Record<string, unknown>
    cancelled_appointment: Record<string, unknown> | null
    triage: { id: string | null; status: unknown } | null
  }> {
    const payload = await agendaFetch<unknown>(
      `/agendamentos/${encodeURIComponent(id)}/reagendar`,
      {
        method: 'POST',
        body: JSON.stringify(withSinglePatientEmail(body)),
      }
    )
    const data = unwrapIaApiData(payload)
    if (!data || typeof data !== 'object') {
      throw new Error('Não foi possível confirmar o reagendamento. Tente novamente.')
    }
    const row = data as Record<string, unknown>

    const appointment =
      row.appointment && typeof row.appointment === 'object' && !Array.isArray(row.appointment)
        ? (row.appointment as Record<string, unknown>)
        : row

    const cancelled_appointment =
      row.cancelled_appointment &&
      typeof row.cancelled_appointment === 'object' &&
      !Array.isArray(row.cancelled_appointment)
        ? (row.cancelled_appointment as Record<string, unknown>)
        : null

    let triage: { id: string | null; status: unknown } | null = null
    if (row.triage && typeof row.triage === 'object' && !Array.isArray(row.triage)) {
      const t = row.triage as Record<string, unknown>
      const triageId = t.id != null ? String(t.id).trim() || null : null
      triage = { id: triageId, status: t.status ?? null }
    }

    return { appointment, cancelled_appointment, triage }
  },

  async cancel(id: string, input: SupportCenterAgendaCancelInput): Promise<void> {
    const canal = input.canal_cancelamento
    if (canal !== 'whatsapp' && canal !== 'telefone' && canal !== 'videochamada') {
      throw new Error('Selecione o canal do cancelamento (Telefone, WhatsApp ou Videochamada).')
    }
    const body: SupportCenterAgendaCancelInput = {
      excluido_por: input.excluido_por.trim(),
      id_motivo_cancelamento: input.id_motivo_cancelamento.trim(),
      status: 'cancelado',
      canal_cancelamento: canal,
    }
    if (!body.excluido_por) {
      throw new Error('Usuário responsável pelo cancelamento não identificado.')
    }
    if (!body.id_motivo_cancelamento) {
      throw new Error('Motivo de cancelamento não informado.')
    }
    await agendaFetch<unknown>(`/agendamentos/${encodeURIComponent(id)}/cancelar`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
}
